import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import React, { useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useIntl } from "react-intl";
import {
  CustomInput,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Modal,
  ModalBody,
  Pagination,
  PaginationItem,
  PaginationLink,
  Table,
  UncontrolledDropdown,
} from "reactstrap";
import { TiktokIcon } from "../social/Tiktok/TiktokIcon";
import {
  formatDuration,
  TikTokAnalyticsSnapshot,
  TikTokPostRow,
  TikTokTimeSeriesDay,
  TikTokTrendDir,
} from "./tiktokAnalyticsMock";
import {
  TIKTOK_BLACK,
  TIKTOK_BLACK_RGB,
  TIKTOK_RED,
  TIKTOK_RED_RGB,
} from "./tiktokBrandColors";
import "./TikTokAnalytics.scss";

interface Props {
  snapshot: TikTokAnalyticsSnapshot;
}

const formatKpiNum = (n: number, decimals = 0) =>
  new Intl.NumberFormat("es-CL", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  }).format(n);

type TikTokKpiTone =
  | "followers"
  | "posts"
  | "balance"
  | "views"
  | "likes"
  | "comments"
  | "shares"
  | "interactions";

function kpiIconForTone(tone: TikTokKpiTone): IconProp {
  const map: Record<TikTokKpiTone, IconProp> = {
    followers: ["fas", "users"],
    posts: ["fas", "photo-video"],
    balance: ["fas", "arrows-alt-v"],
    views: ["fas", "eye"],
    likes: ["fas", "heart"],
    comments: ["fas", "comment"],
    shares: ["fas", "share"],
    interactions: ["fas", "chart-line"],
  };
  return map[tone];
}

function deltaClassForDirection(dir?: TikTokTrendDir): "pos" | "neg" | "flat" | null {
  if (dir === undefined) return null;
  if (dir === "up") return "pos";
  if (dir === "down") return "neg";
  return "flat";
}

function TikTokKpiPillView(props: {
  value: number;
  labelKey: string;
  direction?: TikTokTrendDir;
  tone: TikTokKpiTone;
  decimals?: number;
}) {
  const intl = useIntl();
  const deltaTone = deltaClassForDirection(props.direction);
  const deltaIcon: IconProp =
    props.direction === "down"
      ? ["fas", "arrow-down"]
      : props.direction === "flat"
      ? ["fas", "minus"]
      : ["fas", "arrow-up"];

  return (
    <div className="tiktok-kpi-bento">
      <div className="tiktok-kpi-bento__head">
        <div className="tiktok-kpi-bento__icon-wrap" aria-hidden>
          <FontAwesomeIcon icon={kpiIconForTone(props.tone)} fixedWidth />
        </div>
        {deltaTone !== null ? (
          <span
            className={cn("tiktok-kpi-bento__delta", `tiktok-kpi-bento__delta--${deltaTone}`)}
          >
            <FontAwesomeIcon icon={deltaIcon} />
          </span>
        ) : null}
      </div>
      <div className="tiktok-kpi-bento__body">
        <span className="tiktok-kpi-bento__value">
          {formatKpiNum(props.value, props.decimals ?? 0)}
        </span>
        <span className="tiktok-kpi-bento__label">
          {intl.formatMessage({ id: props.labelKey })}
        </span>
      </div>
    </div>
  );
}

function useNumericSeries(
  days: TikTokTimeSeriesDay[],
  key: keyof TikTokTimeSeriesDay
): number[] {
  return days.map((d) => Number(d[key] ?? 0) || 0);
}

/** Línea + barras posts (rejilla día a día). */
function TikTokLineBarChart(props: {
  days: TikTokTimeSeriesDay[];
  lineVals: number[];
  barVals: number[];
  barColorRgb?: string;
  lineColorHex?: string;
  yTicks: number[];
}) {
  const {
    days,
    lineVals,
    barVals,
    barColorRgb = TIKTOK_RED_RGB,
    lineColorHex = TIKTOK_BLACK,
    yTicks,
  } = props;
  const n = Math.max(days.length, 2);
  const yMin = Math.min(...yTicks);
  const yMax = Math.max(...yTicks);
  const span = Math.max(1e-6, yMax - yMin);
  const barMax = Math.max(1e-6, Math.max(...barVals, ...[1]));

  const pts =
    lineVals
      .map((val, i) => {
        const x = (i / (Math.max(lineVals.length, 2) - 1)) * 100;
        const y = ((yMax - val) / span) * 92 + 4;
        return `${x},${y}`;
      })
      .join(" ");

  return (
    <div
      className="tiktok-chart-shell"
      style={{ "--tiktok-days": n } as React.CSSProperties}
    >
      <div className="tiktok-chart-shell__y">
        {[...yTicks].reverse().map((t) => (
          <span key={t}>{formatKpiNum(t)}</span>
        ))}
      </div>
      <div className="tiktok-chart-shell__body">
        <div className="tiktok-chart-shell__bars-row">
          {barVals.map((b, idx) => (
            <div
              key={`b-${days[idx]?.iso}-${idx}`}
              className="tiktok-chart-shell__bar"
              style={{
                height: `${Math.min(100, (b / barMax) * 70)}%`,
                backgroundColor: `rgba(${barColorRgb}, 0.62)`,
              }}
            />
          ))}
        </div>
        <svg
          className="tiktok-chart-shell__line-svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polyline
            fill="none"
            stroke={lineColorHex}
            strokeWidth={1.15}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            points={pts}
          />
        </svg>
      </div>
      <div className="tiktok-chart-shell__x">
        {days.map((d) => (
          <span key={d.iso}>{d.label}</span>
        ))}
      </div>
    </div>
  );
}

function TikTokBalanceBars(props: { days: TikTokTimeSeriesDay[] }) {
  const nets = props.days.map((d) => Number(d.netFollowers ?? 0));
  const maxMag = Math.max(1e-6, Math.max(...nets.map((v) => Math.abs(v))));
  const n = Math.max(props.days.length, 2);
  const redRgb = TIKTOK_RED_RGB;
  const blackRgb = TIKTOK_BLACK_RGB;

  return (
    <div
      className="tiktok-chart-shell tiktok-chart-shell--bars-only"
      style={{ "--tiktok-days": n } as React.CSSProperties}
    >
      <div className="tiktok-chart-shell__y">
        {[3, 2, 1, 0].map((t) => (
          <span key={t}>{t}</span>
        ))}
      </div>
      <div className="tiktok-chart-shell__body">
        <div className="tiktok-chart-shell__bars-row">
          {nets.map((v, idx) => {
            const pct = Math.min(100, (Math.abs(v) / maxMag) * 76);
            return (
              <div
                key={`n-${props.days[idx]?.iso}-${idx}`}
                className="tiktok-chart-shell__bar"
                style={{
                  height: `${pct}%`,
                  backgroundColor:
                    v < 0 ? `rgba(${blackRgb}, 0.75)` : `rgba(${redRgb}, 0.68)`,
                }}
              />
            );
          })}
        </div>
      </div>
      <div className="tiktok-chart-shell__x">
        {props.days.map((d) => (
          <span key={d.iso}>{d.label}</span>
        ))}
      </div>
    </div>
  );
}

function TikTokInteractionsChart(props: { days: TikTokTimeSeriesDay[] }) {
  const ints = props.days.map((d) => d.interactions ?? 0);
  const likes = props.days.map((d) => d.likes ?? 0);
  const comm = props.days.map((d) => d.comments ?? 0);
  const shar = props.days.map((d) => d.shares ?? 0);
  const yMax =
    Math.max(6, Math.max(...ints, ...likes, ...comm, ...shar, 1) + 1) || 6;
  const n = props.days.length;
  const toPts = (arr: number[], color: string, dash?: string) => {
    const pts = arr.map((val, i) => {
      const x = (i / Math.max(n - 1, 1)) * 100;
      const y = ((yMax - val) / yMax) * 90 + 5;
      return `${x},${y}`;
    });
    return { pts: pts.join(" "), color, dash };
  };

  const s1 = toPts(ints, TIKTOK_BLACK);
  const s2 = toPts(likes, TIKTOK_RED);
  const s3 = toPts(comm, TIKTOK_BLACK, "2.8 3.2");
  const s4 = toPts(shar, TIKTOK_RED, "5 5");
  const barVals = props.days.map((d) => d.postsCount ?? 0);
  const barMax = Math.max(1e-6, Math.max(...barVals, 1));

  return (
    <div
      className="tiktok-chart-shell"
      style={{ "--tiktok-days": n } as React.CSSProperties}
    >
      <div className="tiktok-chart-shell__y">
        {[6, 4, 2, 0].map((t) => (
          <span key={t}>{t}</span>
        ))}
      </div>
      <div className="tiktok-chart-shell__body">
        <div className="tiktok-chart-shell__bars-row">
          {barVals.map((b, idx) => (
            <div
              key={`ib-${props.days[idx]?.iso}-${idx}`}
              className="tiktok-chart-shell__bar"
              style={{
                height: `${Math.min(100, (b / barMax) * 62)}%`,
                backgroundColor: `rgba(${TIKTOK_RED_RGB}, 0.5)`,
              }}
            />
          ))}
        </div>
        <svg className="tiktok-multi-line-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <polyline
            fill="none"
            stroke={s4.color}
            strokeDasharray={s4.dash}
            strokeWidth={0.9}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            points={s4.pts}
          />
          <polyline
            fill="none"
            stroke={s3.color}
            strokeDasharray={s3.dash}
            strokeWidth={0.9}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            points={s3.pts}
          />
          <polyline
            fill="none"
            stroke={s2.color}
            strokeWidth={1}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            points={s2.pts}
          />
          <polyline
            fill="none"
            stroke={s1.color}
            strokeWidth={1.15}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            points={s1.pts}
          />
        </svg>
      </div>
      <div className="tiktok-chart-shell__x">
        {props.days.map((d) => (
          <span key={d.iso}>{d.label}</span>
        ))}
      </div>
    </div>
  );
}

function buildPostDailyPoints(post: TikTokPostRow, key: "views" | "likes"): number[] {
  const total = key === "views" ? post.views : post.likes;
  if (total <= 0) return Array.from({ length: 15 }, () => 0);

  const weights =
    key === "views"
      ? [0.58, 0.16, 0.08, 0.05, 0.035, 0.025, 0.02, 0.018, 0.014, 0.012, 0.01, 0.008, 0.006, 0.004, 0.002]
      : [0.8, 0.12, 0.04, 0.02, 0.02];
  const padded = Array.from({ length: 15 }, (_, i) => weights[i] ?? 0);
  let remaining = total;

  return padded.map((weight, index) => {
    if (index === padded.length - 1) return Math.max(0, remaining);
    const value = Math.min(remaining, Math.round(total * weight));
    remaining -= value;
    return value;
  });
}

function TikTokPostMiniChart(props: {
  values: number[];
  yTicks: number[];
  color: string;
}) {
  const max = Math.max(...props.yTicks, ...props.values, 1);
  const points = props.values
    .map((value, index) => {
      const x = (index / Math.max(props.values.length - 1, 1)) * 100;
      const y = ((max - value) / max) * 84 + 8;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="tiktok-post-modal-chart">
      <div className="tiktok-post-modal-chart__y">
        {[...props.yTicks].reverse().map((tick) => (
          <span key={tick}>{formatKpiNum(tick)}</span>
        ))}
      </div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
        <polyline
          fill="none"
          stroke={props.color}
          strokeWidth={1.2}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          points={points}
        />
      </svg>
      <div className="tiktok-post-modal-chart__x">
        <span>{props.values.length ? "Día 1" : ""}</span>
        <span>Día 15</span>
      </div>
    </div>
  );
}

function TikTokPostMetricTile(props: {
  value: string;
  label: string;
  tone?: "red" | "dark";
}) {
  return (
    <div className={cn("tiktok-post-modal-metric", props.tone && `tiktok-post-modal-metric--${props.tone}`)}>
      <strong>{props.value}</strong>
      <span>{props.label}</span>
    </div>
  );
}

function TikTokPhonePreview({ post, accountHandle }: { post: TikTokPostRow; accountHandle: string }) {
  return (
    <div className="tiktok-phone-preview" aria-label="Vista previa TikTok">
      <div className="tiktok-phone-preview__top">
        <span>Siguiendo</span>
        <strong>Para ti</strong>
      </div>
      <div
        className="tiktok-phone-preview__media"
        style={{ background: post.thumbnailColor }}
      >
        <div className="tiktok-phone-preview__card">
          <TiktokIcon active size="sm" />
          <span>{post.typeLabel}</span>
        </div>
        <div className="tiktok-phone-preview__play">
          <FontAwesomeIcon icon={["fas", "play"]} />
        </div>
      </div>
      <div className="tiktok-phone-preview__side">
        <span><FontAwesomeIcon icon={["fas", "heart"]} /></span>
        <span><FontAwesomeIcon icon={["fas", "comment"]} /></span>
        <span><FontAwesomeIcon icon={["fas", "share"]} /></span>
      </div>
      <div className="tiktok-phone-preview__caption">
        <strong>@{accountHandle}</strong>
        <p>{post.caption}</p>
      </div>
      <div className="tiktok-phone-preview__nav">
        <span>Home</span>
        <span>Friends</span>
        <strong>+</strong>
        <span>Inbox</span>
        <span>Profile</span>
      </div>
    </div>
  );
}

function TikTokPostAnalyticsModal(props: {
  post: TikTokPostRow | null;
  accountHandle: string;
  onClose: () => void;
}) {
  const intl = useIntl();
  const post = props.post;
  const views = useMemo(() => (post ? buildPostDailyPoints(post, "views") : []), [post]);
  const likes = useMemo(() => (post ? buildPostDailyPoints(post, "likes") : []), [post]);
  const maxViews = Math.max(...views, 1);
  const maxLikes = Math.max(...likes, 1);

  return (
    <Modal
      isOpen={Boolean(post)}
      toggle={props.onClose}
      centered
      size="xl"
      className="tiktok-post-modal"
      contentClassName="tiktok-post-modal__content"
    >
      {post ? (
        <ModalBody className="tiktok-post-modal__body">
          <button
            type="button"
            className="tiktok-post-modal__close"
            aria-label="Cerrar"
            onClick={props.onClose}
          >
            <FontAwesomeIcon icon={["fas", "times"]} />
          </button>

          <div className="tiktok-post-modal__main">
            <header className="tiktok-post-modal__header">
              <div className="tiktok-thumb" style={{ background: post.thumbnailColor }} />
              <div>
                <h3>{post.caption}</h3>
                <span>{post.dateLabel} {post.timeLabel}</span>
              </div>
            </header>

            <section className="tiktok-post-modal__section">
              <div className="tiktok-post-modal__section-head">
                <h4>Resumen</h4>
                <span>Visualizaciones diarias de los 15 días desde la publicación</span>
              </div>
              <div className="tiktok-post-modal__metric-grid">
                <TikTokPostMetricTile
                  value={formatKpiNum(post.views)}
                  label={intl.formatMessage({ id: "app.tiktokAnalytics.views" })}
                />
                <TikTokPostMetricTile
                  value={formatKpiNum(post.likes + post.comments + post.shares)}
                  label={intl.formatMessage({ id: "app.tiktokAnalytics.interactions" })}
                  tone="dark"
                />
                <TikTokPostMetricTile value="-" label="Alcance" tone="red" />
              </div>
              <TikTokPostMiniChart
                values={views}
                yTicks={[0, Math.round(maxViews / 2), maxViews]}
                color={TIKTOK_RED}
              />
            </section>

            <section className="tiktok-post-modal__section">
              <div className="tiktok-post-modal__section-head">
                <h4>{intl.formatMessage({ id: "app.tiktokAnalytics.interactions" })}</h4>
                <span>{post.typeLabel} · {formatDuration(post.durationSeconds)}</span>
              </div>
              <div className="tiktok-post-modal__metric-grid">
                <TikTokPostMetricTile
                  value={formatKpiNum(post.likes)}
                  label={intl.formatMessage({ id: "app.tiktokAnalytics.likes" })}
                />
                <TikTokPostMetricTile
                  value={formatKpiNum(post.comments)}
                  label={intl.formatMessage({ id: "app.tiktokAnalytics.comments" })}
                  tone="red"
                />
                <TikTokPostMetricTile
                  value={formatKpiNum(post.shares)}
                  label={intl.formatMessage({ id: "app.tiktokAnalytics.shares" })}
                  tone="dark"
                />
              </div>
              <TikTokPostMiniChart
                values={likes}
                yTicks={[0, Math.round(maxLikes / 2), maxLikes]}
                color={TIKTOK_BLACK}
              />
            </section>
          </div>

          <aside className="tiktok-post-modal__preview">
            <h4>Preview</h4>
            <TikTokPhonePreview post={post} accountHandle={props.accountHandle} />
          </aside>
        </ModalBody>
      ) : null}
    </Modal>
  );
}

type ColKey =
  | "posts"
  | "type"
  | "date"
  | "views"
  | "likes"
  | "comments"
  | "shares"
  | "duration";

const ALL_COLS: ColKey[] = [
  "posts",
  "type",
  "date",
  "views",
  "likes",
  "comments",
  "shares",
  "duration",
];

export const TikTokAnalyticsDashboard = ({ snapshot }: Props) => {
  const intl = useIntl();
  const [tab, setTab] = useState<"community" | "posts">("community");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selectedPost, setSelectedPost] = useState<TikTokPostRow | null>(null);
  const [sortViewsDesc, setSortViewsDesc] = useState(true);
  const [visibleCols, setVisibleCols] = useState<Record<ColKey, boolean>>({
    posts: true,
    type: true,
    date: true,
    views: true,
    likes: true,
    comments: true,
    shares: true,
    duration: true,
  });

  const g = snapshot.growth;
  const followerVals = useNumericSeries(g.followersSeries, "followerTotal");
  const postBarVals = useNumericSeries(g.followersSeries, "postsCount");

  const fMin = Math.min(...followerVals);
  const fMax = Math.max(...followerVals);
  const yPad = Math.max(0.25, (fMax - fMin) * 0.08);
  const lo = fMin - yPad;
  const hi = fMax + yPad;
  const spanF = hi - lo || 1;
  const fticks = [0, 1, 2, 3].map((i) => lo + (spanF * i) / 3);

  const filteredRows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let rows = [...snapshot.postsTable];
    if (ql) {
      rows = rows.filter((r) => r.caption.toLowerCase().includes(ql));
    }
    rows.sort((a, b) =>
      sortViewsDesc ? b.views - a.views : a.views - b.views
    );
    return rows;
  }, [q, snapshot.postsTable, sortViewsDesc]);

  const csvHeaders = ALL_COLS.filter((k) => visibleCols[k]).map((k) => ({
    label: intl.formatMessage({ id: `app.tiktokAnalytics.table${cap(k)}` }),
    key:
      k === "posts"
        ? "caption"
        : k === "duration"
        ? "durationFmt"
        : k === "date"
        ? "dateLabel"
        : k,
  }));

  const csvData = filteredRows.map((r) => ({
    caption: r.caption,
    type: r.typeLabel,
    dateLabel: `${r.dateLabel} ${r.timeLabel}`,
    views: r.views,
    likes: r.likes,
    comments: r.comments,
    shares: r.shares,
    durationFmt: formatDuration(r.durationSeconds),
  }));

  const pages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pagedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="tiktok-analytics-root">
      <div className="tiktok-analytics-banner" role="status">
        {intl.formatMessage({ id: "app.tiktokAnalytics.banner" })}
      </div>

      <div className="tiktok-analytics-card">
        <div className="tiktok-analytics-card__head align-items-center">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="tiktok-analytics-tabs">
              <button
                type="button"
                className={cn("tiktok-analytics-tab", {
                  "tiktok-analytics-tab--active": tab === "community",
                })}
                onClick={() => setTab("community")}
              >
                {intl.formatMessage({ id: "app.tiktokAnalytics.tabCommunity" })}
              </button>
              <button
                type="button"
                className={cn("tiktok-analytics-tab", {
                  "tiktok-analytics-tab--active": tab === "posts",
                })}
                onClick={() => setTab("posts")}
              >
                {intl.formatMessage({ id: "app.tiktokAnalytics.tabPosts" })}
              </button>
            </div>
            <span className="tiktok-muted">
              {intl.formatMessage({ id: "app.tiktokAnalytics.sectionCommunityTitle" })}
            </span>
          </div>
          <span className="tiktok-analytics-handle">
            <TiktokIcon active size="sm" />
            {snapshot.accountHandle}
          </span>
        </div>

        {tab === "community" ? (
          <>
            <section>
              <h3 className="tiktok-analytics-section-title">
                {intl.formatMessage({ id: "app.tiktokAnalytics.sectionGrowth" })}
              </h3>
              <div className="tiktok-kpi-cluster tiktok-analytics-metrics-row">
                <TikTokKpiPillView
                  {...g.followers}
                  tone="followers"
                  decimals={g.followers.decimals}
                />
                <TikTokKpiPillView {...g.posts} tone="posts" />
              </div>
              <TikTokLineBarChart
                days={g.followersSeries}
                lineVals={followerVals}
                barVals={postBarVals}
                yTicks={fticks}
              />
            </section>

            <section className="mt-4 pt-3">
              <h3 className="tiktok-analytics-section-title">
                {intl.formatMessage({ id: "app.tiktokAnalytics.sectionBalance" })}
              </h3>
              <div className="tiktok-kpi-cluster tiktok-analytics-metrics-row">
                <TikTokKpiPillView {...snapshot.balance.netPeriod} tone="balance" />
              </div>
              <TikTokBalanceBars days={snapshot.balance.daily} />
            </section>
          </>
        ) : null}

        {tab === "community" ? (
          <>
            <section className="mt-4 pt-3 border-top border-light">
              <h3 className="tiktok-analytics-section-title">
                {intl.formatMessage({ id: "app.tiktokAnalytics.sectionPostsPeriod" })}
              </h3>
              <h4 className="tiktok-muted mb-3" style={{ fontSize: "0.9rem", fontWeight: 800 }}>
                {intl.formatMessage({ id: "app.tiktokAnalytics.sectionViews" })}
              </h4>
              <div className="tiktok-kpi-cluster tiktok-analytics-metrics-row">
                <TikTokKpiPillView
                  value={snapshot.postsPeriod.views.value}
                  labelKey={snapshot.postsPeriod.views.labelKey}
                  direction={snapshot.postsPeriod.views.direction}
                  tone="views"
                />
                <TikTokKpiPillView {...snapshot.postsPeriod.views.posts} tone="posts" />
              </div>
              {(() => {
                const serie = snapshot.postsPeriod.viewsSeries;
                const views = serie.map((d) => d.views ?? 0);
                const posts = serie.map((d) => d.postsCount ?? 0);
                const vmin = Math.min(...views);
                const vmax = Math.max(...views);
                const vp = Math.max(40, (vmax - vmin) * 0.1);
                const vlo = vmin - vp / 2;
                const vhi = vmax + vp / 2;
                const spanV = vhi - vlo || 1;
                const vticks = [0, 1, 2, 3].map((i) => vlo + (spanV * i) / 3);
                return (
                  <TikTokLineBarChart
                    days={serie}
                    lineVals={views}
                    barVals={posts}
                    lineColorHex={TIKTOK_BLACK}
                    barColorRgb={TIKTOK_RED_RGB}
                    yTicks={vticks}
                  />
                );
              })()}
            </section>

            <section className="mt-4 pt-3">
              <h4 className="tiktok-muted mb-3" style={{ fontSize: "0.9rem", fontWeight: 800 }}>
                {intl.formatMessage({ id: "app.tiktokAnalytics.sectionInteractions" })}
              </h4>
              <div className="tiktok-kpi-cluster tiktok-analytics-metrics-row">
                <TikTokKpiPillView {...snapshot.postsPeriod.interactions.total} tone="interactions" />
                <TikTokKpiPillView {...snapshot.postsPeriod.interactions.likes} tone="likes" />
                <TikTokKpiPillView {...snapshot.postsPeriod.interactions.comments} tone="comments" />
                <TikTokKpiPillView {...snapshot.postsPeriod.interactions.shares} tone="shares" />
                <TikTokKpiPillView {...snapshot.postsPeriod.interactions.posts} tone="posts" />
              </div>
              <TikTokInteractionsChart days={snapshot.postsPeriod.interactionsSeries} />
            </section>
          </>
        ) : null}

        {tab === "posts" ? (
          <section className="pt-2">
            <h3 className="tiktok-analytics-section-title">
              {intl.formatMessage({ id: "app.tiktokAnalytics.tabPosts" })}
            </h3>

            <div className="tiktok-analytics-toolbar">
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <FontAwesomeIcon icon={["fas", "search"]} />
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  placeholder={intl.formatMessage({
                    id: "app.tiktokAnalytics.searchPlaceholder",
                  })}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setPage(1);
                  }}
                />
              </InputGroup>

              <CSVLink
                data={csvData}
                headers={csvHeaders}
                filename={`tiktok-posts-${Date.now()}.csv`}
                className="btn btn-outline-secondary rounded-pill"
                target="_blank"
              >
                <FontAwesomeIcon icon={["fas", "download"]} className="me-2" />
                {intl.formatMessage({ id: "app.tiktokAnalytics.downloadCsv" })}
              </CSVLink>

              <UncontrolledDropdown>
                <DropdownToggle caret className="btn btn-outline-secondary rounded-pill">
                  <FontAwesomeIcon icon={["fas", "columns"]} className="me-2" />
                  {intl.formatMessage({ id: "app.tiktokAnalytics.columnsMenu" })}
                </DropdownToggle>
                <DropdownMenu>
                  {ALL_COLS.map((k) => (
                    <DropdownItem toggle={false} key={k}>
                      <CustomInput
                        type="checkbox"
                        id={`tt-col-${k}`}
                        checked={visibleCols[k]}
                        onChange={() =>
                          setVisibleCols((prev) => ({ ...prev, [k]: !prev[k] }))
                        }
                        label={intl.formatMessage({ id: `app.tiktokAnalytics.table${cap(k)}` })}
                      />
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>

            <div className="table-responsive rounded border bg-white tiktok-posts-table-wrap">
              <Table hover className="mb-0 tiktok-posts-table" size="sm">
                <thead className="bg-light">
                  <tr>
                    {visibleCols.posts ? (
                      <th className="tiktok-posts-table__th-caption">
                        {intl.formatMessage({ id: "app.tiktokAnalytics.tablePosts" })}
                      </th>
                    ) : null}
                    {visibleCols.type ? (
                      <th>{intl.formatMessage({ id: "app.tiktokAnalytics.tableType" })}</th>
                    ) : null}
                    {visibleCols.date ? (
                      <th>{intl.formatMessage({ id: "app.tiktokAnalytics.tableDate" })}</th>
                    ) : null}
                    {visibleCols.views ? (
                      <th role="columnheader button" tabIndex={0} onClick={() => setSortViewsDesc((v) => !v)} className="pointer">
                        {intl.formatMessage({ id: "app.tiktokAnalytics.tableViews" })}{" "}
                        <FontAwesomeIcon
                          icon={["fas", sortViewsDesc ? "sort-amount-down" : "sort-amount-up"]}
                        />
                      </th>
                    ) : null}
                    {visibleCols.likes ? (
                      <th>{intl.formatMessage({ id: "app.tiktokAnalytics.tableLikes" })}</th>
                    ) : null}
                    {visibleCols.comments ? (
                      <th>{intl.formatMessage({ id: "app.tiktokAnalytics.tableComments" })}</th>
                    ) : null}
                    {visibleCols.shares ? (
                      <th>{intl.formatMessage({ id: "app.tiktokAnalytics.tableShares" })}</th>
                    ) : null}
                    {visibleCols.duration ? (
                      <th>{intl.formatMessage({ id: "app.tiktokAnalytics.tableDuration" })}</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map(renderPostRow)}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">{intl.formatMessage({ id: "app.tiktokAnalytics.perPage" })}</span>
                <Input
                  type="select"
                  bsSize="sm"
                  style={{ width: 74 }}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[5, 10, 25].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Input>
                <span className="small text-muted">
                  {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, filteredRows.length)} de{" "}
                  {filteredRows.length}
                </span>
              </div>
              <Pagination className="mb-0 flex-wrap justify-content-end">
                <PaginationItem disabled={page <= 1}>
                  <PaginationLink first onClick={() => setPage(1)} />
                </PaginationItem>
                <PaginationItem disabled={page <= 1}>
                  <PaginationLink previous onClick={() => setPage((p) => Math.max(1, p - 1))} />
                </PaginationItem>
                <PaginationItem disabled={page >= pages}>
                  <PaginationLink next onClick={() => setPage((p) => Math.min(pages, p + 1))} />
                </PaginationItem>
                <PaginationItem disabled={page >= pages}>
                  <PaginationLink last onClick={() => setPage(pages)} />
                </PaginationItem>
              </Pagination>
            </div>
          </section>
        ) : null}
      </div>
      <TikTokPostAnalyticsModal
        post={selectedPost}
        accountHandle={snapshot.accountHandle}
        onClose={() => setSelectedPost(null)}
      />
    </div>
  );

  function renderPostRow(r: TikTokPostRow): React.ReactElement {
    return (
      <tr
        key={r.id}
        className="tiktok-post-row"
        role="button"
        tabIndex={0}
        onClick={() => setSelectedPost(r)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setSelectedPost(r);
          }
        }}
      >
        {visibleCols.posts ? (
          <td className="tiktok-posts-table__cell-caption">
            <div className="tiktok-post-cell">
              <div
                className={cn(
                  "tiktok-thumb",
                  /^#(?:fff|ffffff)$/i.test(r.thumbnailColor.trim()) &&
                    "tiktok-thumb--bright"
                )}
                style={{ background: r.thumbnailColor }}
              />
              <div className="tiktok-post-cell__caption-wrap">
                <span className="tiktok-post-cell__text">{r.caption}</span>
                {r.isNew ? (
                  <>
                    {" "}
                    <span className="tiktok-new-badge">
                      {intl.formatMessage({ id: "app.tiktokAnalytics.statusNew" })}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
          </td>
        ) : null}
        {visibleCols.type ? <td>{r.typeLabel}</td> : null}
        {visibleCols.date ? (
          <td>
            <div className="font-weight-bold small">{r.dateLabel}</div>
            <div className="text-muted small" style={{ fontSize: "0.72rem" }}>
              {r.timeLabel}
            </div>
          </td>
        ) : null}
        {visibleCols.views ? <td>{formatKpiNum(r.views)}</td> : null}
        {visibleCols.likes ? <td>{formatKpiNum(r.likes)}</td> : null}
        {visibleCols.comments ? <td>{formatKpiNum(r.comments)}</td> : null}
        {visibleCols.shares ? <td>{formatKpiNum(r.shares)}</td> : null}
        {visibleCols.duration ? <td>{formatDuration(r.durationSeconds)}</td> : null}
      </tr>
    );
  }
};

function cap(s: ColKey): string {
  return s.slice(0, 1).toUpperCase() + s.slice(1);
}
